import React from 'react';
import './scss/native-protocol-print.scss'
import Refresh from '@mui/icons-material/Refresh';
import { Box, Grid, Skeleton, Typography } from '@mui/material';
import _ from 'lodash';
import Table from '../../component/table';
import { MUIDataTableColumnDef } from 'mui-datatables';
import ButtonComponent from '../../component/button/button.component';
import { IOSSwitch } from '../../component/switch/IOSSwitch';
import CommonInput from '../../component/form/common-input.component'
import { apiManage_rbp as apiManage } from '../../request';
import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import LoadingOverlayButton from '../../component/button/loading-overlay-button.component';
import { PrintModuleAuxiliaryModeContext } from './print-module-auxiliary-mode-context';

/**
 * 表单行：纵向 stack 或横向 inline（高级参数：左侧标签+辅助说明，右侧控件）。
 * @param layout - stack：标签与控件上下排列；inline：与设计稿一致的左右排列
 */
function NativeProtocolField(props: {
    label: string;
    hint?: string;
    layout?: 'stack' | 'inline';
    children: React.ReactNode;
}) {
    const { auxiliaryModeEnabled } = React.useContext(PrintModuleAuxiliaryModeContext);
    /** 是否在辅助模式下展示 hint */
    const showHint = auxiliaryModeEnabled && !!props.hint;
    const layout = props.layout ?? 'stack';

    if (layout === 'inline') {
        return (
            <Box className='native-protocol-page__field native-protocol-page__field--inline'>
                <Box className='native-protocol-page__field-text'>
                    <Typography component='div' className='native-protocol-page__field-label'>
                        {props.label}
                    </Typography>
                    {showHint ? (
                        <Typography component='div' className='native-protocol-page__field-hint'>
                            {props.hint}
                        </Typography>
                    ) : null}
                </Box>
                <Box className='native-protocol-page__field-control'>{props.children}</Box>
            </Box>
        );
    }

    return (
        <Box className='native-protocol-page__field'>
            <Typography component='div' className='native-protocol-page__field-label'>
                {props.label}
            </Typography>
            {showHint ? (
                <Typography component='div' className='native-protocol-page__field-hint'>
                    {props.hint}
                </Typography>
            ) : null}
            <Box className='native-protocol-page__field-control'>{props.children}</Box>
        </Box>
    );
}

function NativeProtocolHeartbeatSubtitle(props: { children: React.ReactNode }) {
    const { auxiliaryModeEnabled } = React.useContext(PrintModuleAuxiliaryModeContext);
    if (!auxiliaryModeEnabled) {
        return null;
    }
    return (
        <Typography component='div' className='native-protocol-page__heartbeat-subtitle'>
            {props.children}
        </Typography>
    );
}

class NativeProtocolPrint extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.save = _.debounce(this.save, 500, {
            leading: true,
            trailing: false
        }).bind(this) as any;
    }
    // 打印类型
    private printType = {
        sendorder: 'print_basic_setting_page_rbp_sendorder_tip',
        takeaway: 'print_basic_setting_page_rbp_takeaway_tip',
        returnDishes: 'print_basic_setting_page_rbp_returnDishes_tip',
        payment: 'print_basic_setting_page_rbp_payment_tip'
    }
    public state: State = {
        printQueueData: [],
        // 选择的队列
        selectedPrintQueue: [],
        //被选中的一菜一单打印机
        checkList: [],
        // 被选中的 送厨单打印字体大小的值
        selectFontSizeValue: 0,
        //是否分单打印
        isHouseBillOfLadingPrinters: false,
        // 送厨单头部空行
        selectEmptyLineValue: 0,
        // 送厨单打印菜品名方式
        selectPrintDishNameType: 0,
        // 送厨单Name1和Name2只显示一行
        deliveryListName1AndName2ShowOnlyOneLine: 0,
        // RWA2 控制是否 打印外卖总单的 打印机
        rwa2PrintTakeAwayCheckkList: [],
        // RWA2 控制是否 开关
        printTakeAwayCheck: false,
        // CWA2 控制是否 打印外卖总单的 打印机
        cwa2PrintTakeAwayCheckkList: [],
        // CWA2 控制是否开关
        cwa2PrintTakeAwayCheck: false,
        // 是否开启打印心跳机制时间
        isHeartbeatMechanism: false,
        // 打印心跳机制时间
        heartbeatMechanism: 0,
        // 原始打印心跳机制时间
        originHeartbeatMechanism: 0,
        // 是否打印预结单
        isPrintAdvanceStatement: false,
        // 是否打印免费菜品
        isPrintFreeDishes: false,
        // 打印预结单的 打印机
        isPrintAdvanceStatementPrinters: [],
        // 删除确认框
        alertDialogOpen: false,
        pageLoading: true,
        refreshLoading: false,
    }

    // *********************
    // Life Cycle Function
    // *********************
    componentDidMount() {
        this.init()
    }

    // *********************
    // Default Function
    // *********************
    /**
     * 获取
     */
    async init() {
        try {
            await Promise.all([
                this.getPrintQueue(),
                this.getSetting(),
            ]);
        } finally {
            this.setState({ pageLoading: false });
        }
    }

    /**
     * 获取设置
     */
    async getSetting() {
        const allSetting = await apiManage.getSetting();
        // $ 一菜一单打印机 rule-one-dish-one-order: 12
        const ruleOneDishOneOrder = this.getSettingValue(allSetting, 12);
        const checkList = ruleOneDishOneOrder?.printer?.map((item: SettingPrinterItem) => {
            return `${item.print_device_id + '、' + item.print_device_name}`
        }) || [];
        // $ 送厨单打印字体大小设置 rule-printer-font-size: 11
        const selectFontSizeValue = this.getSettingValue(allSetting, 11)?.quantity;
        // $ 自主点菜分单打印 13
        const isHouseBillOfLadingPrinters = this.getSettingValue(allSetting, 13)?.status;
        // $ 获取送厨单头部空行 24
        const selectEmptyLineValue = this.getSettingValue(allSetting, 24);
        // $ 获取需要显示的极速点餐精确搜索规则 25
        const selectPrintDishNameType = this.getSettingValue(allSetting, 25);
        // $ 送厨单Name1和Name2只显示一行 28
        const deliveryListName1AndName2ShowOnlyOneLine = this.getSettingValue(allSetting, 28)?.line;
        // $ RWA2 打印外卖总单 rwa2-take-away-check-print 30
        const rwa2takeAwayCheckPrint = this.getSettingValue(allSetting, 30);
        const rwa2PrintTakeAwayCheckkList = rwa2takeAwayCheckPrint?.printer?.map((item: SettingPrinterItem) => {
            return `${item.print_device_id + '、' + item.print_device_name + '、' + item.printer_name}`
        }) || [];
        const printTakeAwayCheck = !!rwa2takeAwayCheckPrint?.enable;
        // $ CWA2控制是否 打印外卖总单 rwa2-take-away-check-print 31
        const caw2takeAwayCheckPrint = this.getSettingValue(allSetting, 31);
        const cwa2PrintTakeAwayCheckkList = caw2takeAwayCheckPrint?.printer?.map((item: SettingPrinterItem) => {
            return `${item.print_device_id + '、' + item.print_device_name + '、' + item.printer_name}`
        }) || [];
        const cwa2PrintTakeAwayCheck = !!caw2takeAwayCheckPrint?.enable;
        // $ 打印服务心跳时间间隔 print-heartbeats-times 32
        const printHeartbeatsTimes = this.getSettingValue(allSetting, 32);
        const isHeartbeatMechanism = printHeartbeatsTimes?.enable;
        const heartbeatMechanism = printHeartbeatsTimes?.mins;
        const originHeartbeatMechanism = printHeartbeatsTimes?.mins;

        // $ 获取打印预结单 payment-check-print 34
        const paymentCheckPrint = this.getSettingValue(allSetting, 34);
        const isPrintAdvanceStatement = !!paymentCheckPrint?.enable;
        const isPrintFreeDishes = !!paymentCheckPrint?.showCondiment;
        const isPrintAdvanceStatementPrinters = paymentCheckPrint?.printer?.map((item: { print_device_id: string, print_device_name: string, printer_name: string }) => {
            return `${item.print_device_id + '、' + item.print_device_name + '、' + item.printer_name}`
        }) || [];
        this.setState({
            //被选中的一菜一单打印机
            checkList,
            // 被选中的 送厨单打印字体大小的值
            selectFontSizeValue,
            //是否分单打印
            isHouseBillOfLadingPrinters,
            // 送厨单头部空行
            selectEmptyLineValue,
            // 送厨单打印菜品名方式
            selectPrintDishNameType,
            // 送厨单Name1和Name2只显示一行
            deliveryListName1AndName2ShowOnlyOneLine,
            // RWA2 控制是否 打印外卖总单的 打印机
            rwa2PrintTakeAwayCheckkList,
            // RWA2 控制是否开关
            printTakeAwayCheck,
            // CWA2 控制是否 打印外卖总单的 打印机
            cwa2PrintTakeAwayCheck,
            // CWA2 控制是否开关
            cwa2PrintTakeAwayCheckkList,
            // 是否开启打印心跳机制时间
            isHeartbeatMechanism,
            // 打印心跳机制时间
            heartbeatMechanism,
            // 原始打印心跳机制时间
            originHeartbeatMechanism,
            // 是否打印预结单
            isPrintAdvanceStatement,
            // 是否打印免费菜品
            isPrintFreeDishes,
            // 打印预结单的 打印机
            isPrintAdvanceStatementPrinters,
        })
    }

    /**
     * 获取设置中的值
     */
    getSettingValue(allSetting: any[], id: number) {
        return _.find(allSetting, (n) => { return n?.id === id })?.value;
    }

    /**
     * 获取所有打印队列
     */
    async getPrintQueue() {
        const res = await apiManage.getPrintersQueue();
        if (!res?.error) {
            this.setState({
                printQueueData: res || [],
                selectedPrintQueue: []
            })
        } else {
            GlobalNotification.error(i18n.t('print_native_protocol_page_get_print_list_error_message'));
        }
    }

    /** 手动刷新队列列表（带按钮 loading，首屏 init 仍直接调 getPrintQueue） */
    refreshPrintQueue = async () => {
        this.setState({ refreshLoading: true });
        try {
            await this.getPrintQueue();
        } finally {
            this.setState({ refreshLoading: false });
        }
    };

    /**
     * 删除付款队列数据
     * @returns
     */
    async deletePrintQueueData() {
        const printId = this.state.selectedPrintQueue.map((data: any) => data.id);
        const res = await apiManage.delPrintersQueue(printId);
        if (!res?.error) {
            GlobalNotification.success(i18n.t('google_text_delete_success'));
        } else {
            GlobalNotification.error(i18n.t('google_text_delete_failed'));
        }
        this.setState({ selectedPrintQueue: [] });
    }
    /**
     * 保存（仅本页可编辑项：送厨单字体 11、头部空行 24、Name1/Name2 行数 28、心跳 32；并发 PATCH）
     */
    async save() {
        try {
            const {
                selectFontSizeValue,
                selectEmptyLineValue,
                deliveryListName1AndName2ShowOnlyOneLine,
                isHeartbeatMechanism,
                heartbeatMechanism,
                originHeartbeatMechanism,
            } = this.state;

            const [res11, res24, res28, res32] = await Promise.all([
                apiManage.patchSettingById(11, {
                    item_name1: selectFontSizeValue,
                    item_name2: selectFontSizeValue,
                    quantity: selectFontSizeValue,
                    table: selectFontSizeValue,
                }),
                apiManage.patchSettingById(24, { stringValue: selectEmptyLineValue }),
                apiManage.patchSettingById(28, { line: deliveryListName1AndName2ShowOnlyOneLine }),
                apiManage.patchSettingById(32, {
                    enable: isHeartbeatMechanism,
                    mins: heartbeatMechanism,
                }),
            ]);

            if (res11.error || res24.error || res28.error || res32.error) {
                GlobalNotification.error(i18n.t('global_text_update_failure'));
                return;
            }

            if (originHeartbeatMechanism !== heartbeatMechanism) {
                GlobalNotification.success(i18n.t('print_basic_setting_page_heartbeat_mechanism_success_tips'));
            }

            GlobalNotification.success(i18n.t('global_text_update_success'));
        }
        catch (error) {
            GlobalNotification.error(i18n.t('global_text_update_failure'));
            console.log(error)
        }
    }

    handleReprint = (_dataIndex: number) => {
        console.log('handleReprint', _dataIndex);

        GlobalNotification.info(i18n.t('print_native_protocol_page_reprint_tip'));
    };

    private getHeartbeatMinuteItems() {
        return _.range(1, 61).map((n) => ({
            label: i18n.t('print_native_protocol_page_heartbeat_minute_label', { n }),
            value: n,
        }));
    }

    // *********************
    // View
    // *********************

    render() {
        if (this.state.pageLoading) {
            return <NativeProtocolPrintPageSkeleton />;
        }
        const { printQueueData, alertDialogOpen, selectedPrintQueue, selectFontSizeValue,
            selectEmptyLineValue, deliveryListName1AndName2ShowOnlyOneLine, isHeartbeatMechanism,
            heartbeatMechanism, refreshLoading,
        } = this.state;
        const columns: MUIDataTableColumnDef[] = [
            {
                name: 'rbp_small_ticket_type',
                label: i18n.t('print_basic_setting_page_small_ticket_type'),
                options: {
                    sort: false,
                    customBodyRenderLite: (dataIndex: number) => {
                        const type: any = printQueueData?.[dataIndex].print_type;
                        const text = _.get(this.printType, type);
                        return text ? i18n.t(text) : type;
                    }
                }
            },
            {
                name: 'print_device_name',
                label: i18n.t('print_native_protocol_page_column_printer'),
                options: {
                    sort: false
                }
            },
            {
                name: 'table_ref',
                label: i18n.t('print_native_protocol_page_table_ref'),
                options: {
                    sort: false,
                    customBodyRenderLite: (dataIndex: number) => {
                        const row: any = printQueueData?.[dataIndex] || {};
                        const t = row.table != null ? String(row.table) : '';
                        const c = row.customer_name != null ? String(row.customer_name) : '';
                        const o = row.order_head_id != null ? String(row.order_head_id) : '';
                        const right = c || o;
                        const s = [t, right].filter(Boolean).join(' / ');
                        return s || '—';
                    },
                }
            },
            {
                name: 'order_time',
                label: i18n.t('print_basic_setting_page_order_time'),
                options: {
                    sort: false,
                }
            },
            {
                name: 'dishes',
                label: i18n.t('print_basic_setting_page_dishName'),
                options: {
                    sort: false,
                    customBodyRenderLite: (dataIndex: number) => {
                        const printDishData: any = printQueueData?.[dataIndex].dishes || {};
                        let dishName = '';
                        Object.keys(printDishData).forEach(turn => {
                            (printDishData[turn] || []).forEach((dish: any) => {
                                if (dish.item_name1) dishName += dish.item_name1 + '<br/>';
                            })
                        })
                        return <div dangerouslySetInnerHTML={{ __html: dishName }}></div>;
                    }
                }
            },
            {
                name: 'status',
                label: i18n.t('print_queue_page_print_status'),
                options: {
                    sort: false,
                    customBodyRenderLite: (dataIndex: number) => {
                        const row: any = printQueueData?.[dataIndex] || {};
                        const raw = row.status ?? row.print_status;
                        const text = raw != null && String(raw).trim() !== ''
                            ? String(raw)
                            : i18n.t('print_native_protocol_page_status_success');
                        const okClass = !raw || String(raw).trim() === '' ? 'native-protocol-page__status--ok' : '';
                        return <span className={okClass}>{text}</span>;
                    },
                }
            },
            {
                name: 'action',
                label: i18n.t('print_native_protocol_page_col_action'),
                options: {
                    sort: false,
                    customBodyRenderLite: (dataIndex: number) => (
                        <span
                            className='native-protocol-page__link'
                            onClick={() => { this.handleReprint(dataIndex); }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    this.handleReprint(dataIndex);
                                }
                            }}
                            role='button'
                            tabIndex={0}
                        >
                            {i18n.t('print_queue_page_double_striking')}
                        </span>
                    ),
                }
            },
        ]
        // 送厨单打印字体大小的值
        const selectFontSizeValueOptions = [{
            label: '0',
            value: 0
        }, {
            label: '0.5',
            value: 0.5
        }, {
            label: '1',
            value: 1
        }, {
            label: '2',
            value: 2
        }]
        return (
            <div className='native-protocol-page native-protocol-page--shell'>
                <Grid container spacing={2} className='native-protocol-page__grid'>
                    <Grid item xs={12} lg={7}>
                        <Box className='native-protocol-page__card'>
                            <Box className='native-protocol-page__card-head'>
                                <Typography component='h2' className='native-protocol-page__card-title'>
                                    {i18n.t('print_native_protocol_page_realtime_queue')}
                                </Typography>
                                <Box className='native-protocol-page__card-actions'>
                                    <ButtonComponent
                                        titleText={i18n.t('google_text_public_button_delete')}
                                        handleClick={() => { this.setState({ alertDialogOpen: true }); }}
                                        action='DELETE'
                                        disabled={selectedPrintQueue.length === 0}
                                    />
                                    <LoadingOverlayButton
                                        type='button'
                                        variant='contained'
                                        className='native-protocol-page__btn-primary'
                                        onClick={() => { void this.refreshPrintQueue(); }}
                                        loading={refreshLoading}
                                        loadingIndicatorSize={18}
                                        sx={{
                                            minWidth: 36,
                                            width: 36,
                                            height: 36,
                                            p: 0,
                                            borderRadius: '8px',
                                        }}
                                    >
                                        <Refresh sx={{ fontSize: 20 }} />
                                    </LoadingOverlayButton>
                                </Box>
                            </Box>
                            <Box className='native-protocol-page__table-wrap'>
                                <Table
                                    data={printQueueData || []}
                                    columns={columns}
                                    selectableRowsHideCheckboxes={false}
                                    rowsSelected={selectedPrintQueue}
                                    onRowSelectionChange={(
                                        _currentRowsSelected: any[],
                                        _allRowsSelected: any[],
                                        rowsSelected?: any[]
                                    ) => {
                                        this.setState({
                                            selectedPrintQueue: rowsSelected || []
                                        });
                                    }}
                                />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={12} lg={5}>
                        <Box className='native-protocol-page__card'>
                            <Box className='native-protocol-page__section'>
                                <Typography component='div' className='native-protocol-page__section-title'>
                                    {i18n.t('print_native_protocol_page_advanced')}
                                </Typography>
                                <Box className='native-protocol-page__save-row'>
                                    <ButtonComponent
                                        titleText={i18n.t('global_btn_save')}
                                        handleClick={async () => { await this.save(); }}
                                        action='SAVE'
                                        space={false}
                                    />
                                </Box>
                            </Box>
                            <NativeProtocolField
                                layout='inline'
                                label={i18n.t('print_native_protocol_page_send_label_size')}
                                hint={i18n.t('print_native_protocol_page_advanced_aux_font_size')}
                            >
                                <CommonInput
                                    inputType='SELECT'
                                    items={selectFontSizeValueOptions.map((o) => ({ label: String(o.label), value: o.value }))}
                                    value={selectFontSizeValue}
                                    onChange={(e: any) => { this.setState({ selectFontSizeValue: Number(e.target.value) }); }}
                                    variant='outlined'
                                    fullWidth
                                    hiddenLabel
                                />
                            </NativeProtocolField>
                            <NativeProtocolField
                                layout='inline'
                                label={i18n.t('print_native_protocol_page_send_head_empty_line')}
                                hint={i18n.t('print_native_protocol_page_advanced_aux_head_empty')}
                            >
                                <CommonInput
                                    type='number'
                                    variant='outlined'
                                    fullWidth
                                    hiddenLabel
                                    value={selectEmptyLineValue}
                                    onChange={(e) => {
                                        const value = Number(e.target.value);
                                        if (value >= 0 && value <= 20) {
                                            this.setState({
                                                selectEmptyLineValue: Number(value.toFixed(0))
                                            });
                                        }
                                    }}
                                />
                            </NativeProtocolField>
                            <NativeProtocolField
                                layout='inline'
                                label={i18n.t('print_native_protocol_page_send_dish_name_print_line_count')}
                                hint={i18n.t('print_native_protocol_page_advanced_aux_dish_lines')}
                            >
                                <CommonInput
                                    type='number'
                                    variant='outlined'
                                    fullWidth
                                    hiddenLabel
                                    value={deliveryListName1AndName2ShowOnlyOneLine}
                                    onChange={(e) => {
                                        const value = Number(e.target.value);
                                        if (value >= 0 && value <= 10) {
                                            this.setState({
                                                deliveryListName1AndName2ShowOnlyOneLine: Number(value.toFixed(0))
                                            });
                                        }
                                    }}
                                />
                            </NativeProtocolField>
                            <Box className='native-protocol-page__heartbeat-block'>
                                <Box className='native-protocol-page__heartbeat-header-row'>
                                    <Box className='native-protocol-page__heartbeat-titles'>
                                        <Typography component='div' className='native-protocol-page__heartbeat-title'>
                                            {i18n.t('print_native_protocol_page_print_service_heartbeat_time_internal')}
                                        </Typography>
                                        <NativeProtocolHeartbeatSubtitle>
                                            {i18n.t('print_native_protocol_page_heartbeat_subtitle')}
                                        </NativeProtocolHeartbeatSubtitle>
                                    </Box>
                                    <IOSSwitch
                                        checked={isHeartbeatMechanism}
                                        onChange={() => {
                                            this.setState({ isHeartbeatMechanism: !isHeartbeatMechanism });
                                        }}
                                    />
                                </Box>
                                <Box className='native-protocol-page__heartbeat-select'>
                                    <CommonInput
                                        inputType='SELECT'
                                        items={this.getHeartbeatMinuteItems()}
                                        value={heartbeatMechanism}
                                        onChange={(e: any) => {
                                            this.setState({ heartbeatMechanism: Number(e.target.value) });
                                        }}
                                        variant='outlined'
                                        fullWidth
                                        hiddenLabel
                                        disabled={!isHeartbeatMechanism}
                                    />
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                <AlertDialog
                    open={alertDialogOpen}
                    variant="delete"
                    title={i18n.t('print_basic_setting_page_delete_queie')}
                    onCancel={() => {
                        this.setState({ alertDialogOpen: false });
                    }}
                    onConfirm={() => {
                        this.setState({ alertDialogOpen: false });
                        this.deletePrintQueueData();
                    }}
                />
            </div>
        );
    }
}

/**
 * 首屏 `init` 完成前占位：`lg={7}` 队列卡片（含表头操作区）、`lg={5}` 高级参数 + 心跳，与实页结构一致。
 */
function NativeProtocolPrintPageSkeleton() {
    /** 右侧高级参数单行占位（左文案列 + 右约 200px 控件，贴合 `.native-protocol-page__field--inline`） */
    const advancedRowSkeletonKeys = [1, 2, 3];

    return (
        <div className='native-protocol-page native-protocol-page--shell native-protocol-page--skeleton'>
            <Grid container spacing={2} className='native-protocol-page__grid'>
                <Grid item xs={12} lg={7}>
                    <Box className='native-protocol-page__card' sx={{ p: 2, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                        {/* 与 `native-protocol-page__card-head` 一致：标题 + 删除 / 刷新 */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                mb: 2,
                                flexShrink: 0,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Skeleton variant='text' width={280} height={32} sx={{ flexShrink: 0 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Skeleton variant='rounded' width={72} height={36} sx={{ borderRadius: 1 }} />
                                <Skeleton variant='rounded' width={36} height={36} sx={{ borderRadius: '8px' }} />
                            </Box>
                        </Box>
                        <Skeleton
                            variant='rounded'
                            sx={{ flex: 1, minHeight: 220, borderRadius: 1, width: '100%' }}
                        />
                    </Box>
                </Grid>
                <Grid item xs={12} lg={5}>
                    <Box
                        className='native-protocol-page__card'
                        sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}
                    >
                        {/* `native-protocol-page__section`：区块标题 + 保存 */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1.5,
                                flexWrap: 'wrap',
                                flexShrink: 0,
                            }}
                        >
                            <Skeleton variant='text' width={180} height={28} sx={{ flexShrink: 0 }} />
                            <Skeleton variant='rounded' width={88} height={36} sx={{ borderRadius: 1 }} />
                        </Box>
                        {advancedRowSkeletonKeys.map((k) => (
                            <Box
                                key={`sk-np-inline-${k}`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    flexShrink: 0,
                                }}
                            >
                                <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Skeleton variant='text' sx={{ width: '85%', height: 22 }} />
                                    <Skeleton variant='text' sx={{ width: '70%', height: 18 }} />
                                </Box>
                                <Skeleton variant='rounded' width={200} height={48} sx={{ flexShrink: 0, borderRadius: 1 }} />
                            </Box>
                        ))}
                        {/* `native-protocol-page__heartbeat-block`：首行标题(+副文案占位)+开关，次行全宽下拉 */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flexShrink: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Skeleton variant='text' sx={{ width: '95%', height: 22 }} />
                                    <Skeleton variant='text' sx={{ width: '80%', height: 18 }} />
                                </Box>
                                <Skeleton variant='rounded' width={52} height={32} sx={{ borderRadius: 2, flexShrink: 0 }} />
                            </Box>
                            <Skeleton variant='rounded' height={48} sx={{ borderRadius: 1, width: '100%' }} />
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </div>
    );
}

export default NativeProtocolPrint
// *********************
// State & Props
// *********************

type State = {
    printQueueData: any[];
    selectedPrintQueue: any[];
    checkList: string[],
    selectFontSizeValue: number,
    isHouseBillOfLadingPrinters: boolean;
    selectEmptyLineValue: number;
    selectPrintDishNameType: number;
    deliveryListName1AndName2ShowOnlyOneLine: number;
    rwa2PrintTakeAwayCheckkList: string[];
    cwa2PrintTakeAwayCheckkList: string[];
    isHeartbeatMechanism: boolean;
    heartbeatMechanism: number;
    originHeartbeatMechanism: number;
    isPrintAdvanceStatement: boolean;
    isPrintFreeDishes: boolean;
    isPrintAdvanceStatementPrinters: string[];
    alertDialogOpen: boolean;
    printTakeAwayCheck: boolean;
    cwa2PrintTakeAwayCheck: boolean;
    pageLoading: boolean;
    refreshLoading: boolean;
}

type Props = {}

/** 云端 setting 中带 printer 数组时的打印机条目（拼接本地展示字符串用） */
type SettingPrinterItem = {
    /** 打印机设备 ID */
    print_device_id: number;
    /** 设备展示名称 */
    print_device_name: string;
    /** 打印机逻辑名（部分规则返回） */
    printer_name?: string;
}